import React from 'react';
import { copy, CreditEntry, Language } from '../content/i18n';

interface CreditsProps {
  language: Language;
}

const Credits: React.FC<CreditsProps> = ({ language }) => {
  const t = copy[language].credits;

  return (
    <section
      aria-labelledby="credits-title"
      style={{
        position: 'relative',
        padding: 'clamp(64px, 10vw, 120px) clamp(20px, 5vw, 64px)',
        background:
          'linear-gradient(to bottom, rgba(8,8,8,0) 0%, rgba(8,8,8,0.96) 14%, #050505 100%)',
      }}
    >
      <div style={{ maxWidth: 1120, margin: '0 auto', textAlign: 'center' }}>
        <h2 id="credits-title" style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>
          {t.sectionTitle}
        </h2>

        <div
          style={{
            margin: '0 0 28px',
            padding: '20px clamp(14px, 3vw, 30px) 22px',
            borderTop: '1px solid rgba(196,148,40,0.4)',
            borderBottom: '1px solid rgba(196,148,40,0.22)',
            background:
              'linear-gradient(180deg, rgba(196,148,40,0.09) 0%, rgba(196,148,40,0.03) 32%, rgba(255,255,255,0.01) 100%)',
          }}
        >
          <a
            href="https://www.hanikahmedia.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t.hanikahAria}
            style={{ display: 'block', opacity: 0.9, transition: 'opacity 0.25s ease, transform 0.25s ease', marginBottom: 12 }}
            onMouseEnter={(e) => {
              const target = e.currentTarget as HTMLAnchorElement;
              target.style.opacity = '1';
              target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              const target = e.currentTarget as HTMLAnchorElement;
              target.style.opacity = '0.9';
              target.style.transform = 'translateY(0)';
            }}
          >
            <img
              src="/plan/hanikah-logo.svg"
              alt="Hanikah Media"
              style={{ height: 44, width: 'auto', display: 'block', margin: '0 auto' }}
            />
          </a>

          {/* <p
            style={{
              margin: '8px 0 14px',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '0.48rem',
              letterSpacing: '0.3em',
              color: 'rgba(196,148,40,0.74)',
              textTransform: 'uppercase',
              fontWeight: 600,
            }}
          >
            Hanikah Media
          </p> */}

          <a
            href="https://www.instagram.com/hanikahmedia/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t.hanikahInstagramAria}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              minWidth: 182,
              border: '1px solid rgba(196,148,40,0.5)',
              color: 'rgba(255,255,255,0.86)',
              textDecoration: 'none',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '0.52rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              padding: '8px 14px',
              lineHeight: 1,
              background: 'rgba(8,8,8,0.52)',
              boxShadow: '0 8px 20px rgba(0,0,0,0.24)',
              transition: 'all 0.25s ease',
            }}
            onMouseEnter={(e) => {
              const target = e.currentTarget as HTMLAnchorElement;
              target.style.color = '#fff';
              target.style.borderColor = 'rgba(196,148,40,0.85)';
              target.style.background = 'rgba(196,148,40,0.16)';
              target.style.boxShadow = '0 10px 26px rgba(0,0,0,0.32)';
              target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              const target = e.currentTarget as HTMLAnchorElement;
              target.style.color = 'rgba(255,255,255,0.86)';
              target.style.borderColor = 'rgba(196,148,40,0.5)';
              target.style.background = 'rgba(8,8,8,0.52)';
              target.style.boxShadow = '0 8px 20px rgba(0,0,0,0.24)';
              target.style.transform = 'translateY(0)';
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2.5" y="2.5" width="19" height="19" rx="5" ry="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17.6" cy="6.4" r="0.7" fill="currentColor" stroke="none" />
            </svg>
            <span>{t.hanikahInstagramHandle}</span>
          </a>
        </div>

        <div
          style={{
            width: 72,
            height: 1,
            background: 'rgba(196,148,40,0.5)',
            margin: '0 auto 32px',
          }}
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '26px clamp(18px, 3vw, 36px)',
            alignItems: 'start',
          }}
        >
          {t.entries.map(({ roles, names }: CreditEntry) => (
            <div
              key={`${roles.join('|')}-${names.map(({ name }) => name).join('|')}`}
              style={{
                padding: '22px 18px 20px',
                borderTop: '1px solid rgba(196,148,40,0.28)',
                background: 'linear-gradient(to bottom, rgba(255,255,255,0.02), rgba(255,255,255,0))',
              }}
            >
              <div style={{ margin: '0 0 12px' }}>
                {roles.map((roleLine) => (
                  <p
                    key={`${roleLine}-${names.map(({ name }) => name).join('|')}`}
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: '0.52rem',
                      letterSpacing: '0.34em',
                      color: 'rgba(196,148,40,0.84)',
                      textTransform: 'uppercase',
                      margin: '0 0 4px',
                      fontWeight: 600,
                      lineHeight: 1.55,
                    }}
                  >
                    {roleLine}
                  </p>
                ))}
              </div>

              <div>
                {names.map(({ name, href }) => (
                  <p
                    key={`${roles.join('|')}-${name}`}
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: '1.15rem',
                      fontWeight: 400,
                      color: 'rgba(255,255,255,0.82)',
                      margin: '0 0 4px',
                      letterSpacing: '0.03em',
                      lineHeight: 1.3,
                    }}
                  >
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: 'rgba(255,255,255,0.82)',
                          textDecoration: 'none',
                          borderBottom: '1px solid rgba(196,148,40,0.45)',
                          paddingBottom: 1,
                          transition: 'color 0.25s ease, border-color 0.25s ease',
                        }}
                        onMouseEnter={(e) => {
                          const target = e.currentTarget as HTMLAnchorElement;
                          target.style.color = '#f6efe5';
                          target.style.borderColor = 'rgba(196,148,40,0.85)';
                        }}
                        onMouseLeave={(e) => {
                          const target = e.currentTarget as HTMLAnchorElement;
                          target.style.color = 'rgba(255,255,255,0.82)';
                          target.style.borderColor = 'rgba(196,148,40,0.45)';
                        }}
                      >
                        {name}
                      </a>
                    ) : (
                      name
                    )}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Credits;